import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { getPriceMatchYearlySavings } from '@/features/priceMatching/priceMatching.utils'
import { type ProductOfferFragment } from '@/services/graphql/generated'
import { type PriceIntent } from '@/services/priceIntent/priceIntent.types'
import { useFormatter } from '@/utils/useFormatter'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { type DiscountInfoCardProps } from '../components/DiscountInfoCard'

type DiscountNotificationOffer = Pick<ProductOfferFragment, 'cost' | 'priceMatch'>
type DiscountNotificationPriceIntent = Pick<PriceIntent, 'externalInsurer' | 'hasCollectedInsurelyData'>

export function useDiscountNotification(
  offer: DiscountNotificationOffer,
  priceIntent?: DiscountNotificationPriceIntent,
) {
  const { t } = useTranslation('purchase-form')
  const formatter = useFormatter()

  const discountNotification = useMemo<DiscountInfoCardProps | null>(() => {
    if (offer.priceMatch) {
      const company = offer.priceMatch.externalInsurer.displayName

      if (offer.priceMatch.priceReduction.amount >= 1) {
        const priceReduction = getPriceMatchYearlySavings(offer, formatter)
        if (!priceReduction) return null

        return {
          content: (
            <Trans
              i18nKey="PRICE_MATCH_DISCOUNT_BREAKDOWN_SUCCESS"
              ns="purchase-form"
              values={{ amount: priceReduction, company }}
              components={{ strong: <strong className={sprinkles({ color: 'textPrimary' })} /> }}
            />
          ),
          variant: 'success',
        }
      }

      const currentPrice = formatter.monthlyPrice(offer.priceMatch.externalPrice)
      return {
        content: t('PRICE_MATCH_DISCOUNT_BREAKDOWN_NO_MATCH', { company, amount: currentPrice }),
        variant: 'infoInline',
      }
    } else if (priceIntent?.hasCollectedInsurelyData) {
      const company = priceIntent.externalInsurer?.displayName

      return {
        content: company
          ? t('PRICE_MATCH_DISCOUNT_BREAKDOWN_MATCH_NOT_POSSIBLE', { company })
          : t('PRICE_MATCH_DISCOUNT_BREAKDOWN_MATCH_NOT_POSSIBLE_UNKNOWN_COMPANY'),
        variant: 'infoInline',
      }
    }

    return null
  }, [offer, formatter, t, priceIntent?.hasCollectedInsurelyData, priceIntent?.externalInsurer?.displayName])

  return discountNotification
}
